import { useEffect, useState } from 'react';
import { NavLink } from 'react-router';

/** Android/Chrome's own install prompt, kept until the user asks for it. */
interface InstallPromptEvent extends Event { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e as InstallPromptEvent; listeners.forEach((f) => f()); });
  window.addEventListener('appinstalled', () => { deferred = null; listeners.forEach((f) => f()); });
}

const isStandalone = () => !!window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isPhone = () => !!window.matchMedia?.('(max-width: 760px)').matches;
const DISMISS_KEY = 'crm.install.dismissed';

function useInstall() {
  const [, force] = useState(0);
  useEffect(() => { const f = () => force((n) => n + 1); listeners.add(f); return () => { listeners.delete(f); }; }, []);
  return {
    installed: isStandalone(),
    canPrompt: !!deferred,
    ios: isIos(),
    async install() {
      if (!deferred) return;
      await deferred.prompt();
      await deferred.userChoice.catch(() => undefined);
      deferred = null;
      force((n) => n + 1);
    },
  };
}

function IosSteps() {
  return (
    <ol className="install-steps small">
      <li>Open this page in <b>Safari</b>.</li>
      <li>Tap the <b>Share</b> button <span aria-hidden="true">(□↑)</span>.</li>
      <li>Choose <b>Add to Home Screen</b>, then <b>Add</b>.</li>
    </ol>
  );
}

/** In the account menu: install the app on this device (a menu line like the others). */
export function InstallMenuItem() {
  const a = useInstall();
  const [help, setHelp] = useState(false);
  if (a.installed) return null;
  if (a.canPrompt) return <button type="button" className="menu-link link-btn" onClick={() => void a.install()}>Install the app on this device</button>;
  return (
    <div>
      <button type="button" className="menu-link link-btn" onClick={() => setHelp((v) => !v)}>Install the app on your phone</button>
      {help && (a.ios ? <IosSteps /> : <p className="small muted">On Android, open the CRM in Chrome, tap the ⋮ menu, then <b>Install app</b> (or <b>Add to Home screen</b>).</p>)}
    </div>
  );
}

/** On phones, once: suggest installing the app (hidden after it's installed or dismissed). */
export function InstallBanner() {
  const a = useInstall();
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; } });
  if (hidden || a.installed || !isPhone() || (!a.canPrompt && !a.ios)) return null;
  const dismiss = () => { setHidden(true); try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ } };
  return (
    <div className="install-banner" role="region" aria-label="Install the app">
      <img src="/icon-192.png" alt="" width={40} height={40} />
      <div className="install-text">
        <b>Install Danat ERP on your phone</b>
        {a.canPrompt ? <span className="small muted">Opens full screen from your home screen.</span> : <IosSteps />}
      </div>
      <div className="install-actions">
        {a.canPrompt && <button className="btn small primary" onClick={() => void a.install()}>Install</button>}
        <button className="btn small ghost" onClick={dismiss} aria-label="Not now">Not now</button>
      </div>
    </div>
  );
}

const ICONS: Record<string, string> = {
  home: 'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z',
  deals: 'M4 18l5-6 4 3 7-9M15 6h5v5',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  projects: 'M3 7h7l2 2h9v10H3zM3 7V5h6',
  people: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5M16 11a3 3 0 1 0 0-6M17.5 14.6c2 .6 3.4 2.6 3.9 5.4',
  settings: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1',
};
export interface BottomItem { to: string; label: string; icon: keyof typeof ICONS; end?: boolean }

/** Phones: the main places at the bottom, within thumb reach. */
export function BottomNav({ items }: { items: BottomItem[] }) {
  return (
    <nav className="bottom-nav" aria-label="Quick navigation">
      {items.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => (isActive ? 'active' : '')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[t.icon]} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <span>{t.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
